import { useCallback, useEffect, useState } from "react";
import { parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { Check, X, Pencil, Trash2, ExternalLink, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import EventEditor from "@/pages/_EventEditor";

export default function AdminReview() {
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/events", { params: { status: "pending", q: q || undefined } });
      setEvents(r.data);
    } finally { setLoading(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => setSelected(selected.size === events.length ? new Set() : new Set(events.map(e => e.id)));

  const bulk = async (action) => {
    if (selected.size === 0) return;
    await api.post("/admin/events/bulk", { ids: [...selected], action });
    toast.success(`${action} ${selected.size} event(s)`);
    setSelected(new Set());
    load();
  };
  const one = async (id, action) => {
    await api.post(`/admin/events/${id}/${action}`);
    toast.success(`${action}d`);
    load();
  };

  const toggleFeature = async (id, current) => {
    try {
      const r = await api.post(`/admin/events/${id}/feature`, { featured: !current });
      setEvents(prev => prev.map(x => x.id === id ? { ...x, featured: r.data.featured } : x));
      toast.success(r.data.featured ? "★ Featured on homepage" : "Un-featured");
    } catch (e) {
      toast.error("Failed to toggle featured");
    }
  };

  return (
    <div>
      <div className="flex items-baseline justify-between mb-4">
        <h1 className="font-display text-2xl font-bold tracking-tight text-[#1A3C5A]">Review queue</h1>
        <div className="text-sm text-slate-500">{events.length} pending</div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <Input placeholder="Search title/venue…" value={q} onChange={(e)=>setQ(e.target.value)}
               className="max-w-xs bg-white" data-testid="review-search" />
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => bulk("approve")} disabled={selected.size===0}
                  data-testid="bulk-approve-btn" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50">
            <Check size={14} className="mr-1" /> Approve ({selected.size})
          </Button>
          <Button size="sm" variant="outline" onClick={() => bulk("reject")} disabled={selected.size===0}
                  data-testid="bulk-reject-btn" className="border-slate-400 text-slate-600 hover:bg-slate-50">
            <X size={14} className="mr-1" /> Reject
          </Button>
          <Button size="sm" variant="outline" onClick={() => bulk("delete")} disabled={selected.size===0}
                  data-testid="bulk-delete-btn" className="border-red-500 text-red-600 hover:bg-red-50">
            <Trash2 size={14} className="mr-1" /> Delete
          </Button>
        </div>
      </div>

      <div className="bg-white border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500 font-display">
            <tr>
              <th className="py-2 px-2 w-8"><Checkbox checked={selected.size>0 && selected.size===events.length}
                                                      onCheckedChange={toggleAll} data-testid="select-all" /></th>
              <th className="py-2 px-2 text-left">When</th>
              <th className="py-2 px-2 text-left">Title</th>
              <th className="py-2 px-2 text-left">Venue · Town</th>
              <th className="py-2 px-2 text-left">Category</th>
              <th className="py-2 px-2 text-left">Source</th>
              <th className="py-2 px-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && (
              <tr><td colSpan={7} className="py-12 text-center text-slate-500">
                {loading ? "Loading…" : "Inbox zero. Nothing to review."}
              </td></tr>
            )}
            {events.map(e => (
              <tr key={e.id} className="border-t border-border hover:bg-slate-50" data-testid={`review-row-${e.id}`}>
                <td className="py-1.5 px-2"><Checkbox checked={selected.has(e.id)} onCheckedChange={()=>toggle(e.id)} /></td>
                <td className="py-1.5 px-2 whitespace-nowrap font-mono text-xs">
                  {(() => { try { return formatInTimeZone(parseISO(e.start_datetime), "America/New_York", "MMM d, h:mm a"); } catch { return e.start_datetime; } })()}
                </td>
                <td className="py-1.5 px-2 font-medium">
                  {e.title}
                  {e.is_paid && <Badge variant="outline" className="ml-2 border-amber-300 text-amber-700">Paid</Badge>}
                  {e.source_type === "user_submitted" && (
                    <Badge variant="outline" className="ml-2 border-emerald-500 text-emerald-700 bg-emerald-50">USER SUBMITTED</Badge>
                  )}
                  {e.submitter_email && (
                    <div className="text-xs text-slate-500 font-normal mt-0.5">by {e.submitter_email}</div>
                  )}
                </td>
                <td className="py-1.5 px-2 text-slate-600">{[e.venue, e.town].filter(Boolean).join(" · ")}</td>
                <td className="py-1.5 px-2 text-slate-600">{e.category || "—"}</td>
                <td className="py-1.5 px-2 text-xs text-slate-500">
                  {e.source_type || "manual"}
                  {e.source_url && <a href={e.source_url} target="_blank" rel="noreferrer" className="ml-1 inline-block"><ExternalLink size={10}/></a>}
                </td>
                <td className="py-1.5 px-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={()=>toggleFeature(e.id, e.featured)}
                          data-testid={`feature-btn-${e.id}`}
                          title={e.featured ? "Un-feature" : "Feature on homepage"}
                          className="hover:bg-amber-50">
                    <Star size={13} className={e.featured ? "fill-amber-400 text-amber-500" : "text-slate-400"}/>
                  </Button>
                  <Button size="sm" variant="ghost" onClick={()=>setEditing(e)} data-testid={`edit-${e.id}`}><Pencil size={13}/></Button>
                  <Button size="sm" variant="ghost" onClick={()=>one(e.id,"approve")} data-testid={`approve-${e.id}`}
                          className="text-emerald-700 hover:text-emerald-800"><Check size={13}/></Button>
                  <Button size="sm" variant="ghost" onClick={()=>one(e.id,"reject")} data-testid={`reject-${e.id}`}
                          className="text-slate-500 hover:text-slate-700"><X size={13}/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EventEditor
          event={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
